import debounce from 'lodash-es/debounce';
import fill from 'lodash-es/fill';
import React from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { GridUtils } from 'module/Common';
import { MzMap } from 'module/Model/MzMap';
import { PostList, PostListItem } from 'module/Model/PostList';

type Props = {
  postList: PostList;
  availableWidth: number;
  children: (
    params: GridUtils.GridResult,
    poses: Array<string>,
    onImageLoaded: (portId: number) => void,
    onInitDone: (portId: number) => void,
    registerItemRef: (index: number, ref: HTMLElement | null) => void
  ) => React.ReactElement<any>;
  touch: boolean;
  titleTile?: boolean;
  titleTileHeightRatio?: number;
  requestedPostId: number | null;
  itemGridIsInExitState: boolean;
};

export class GridProvider extends React.PureComponent<Props> {
  private itemsRefs: MzMap<number, HTMLElement | undefined> = MzMap.create();
  private observers: MzMap<number, ResizeObserver> = MzMap.create();
  private loadedImages: MzMap<number, boolean> = MzMap.create();
  private loadingDone: MzMap<number, boolean> = MzMap.create();
  private postsDescriptionHeight: MzMap<number, number> = MzMap.create();
  private unmounted: boolean = false;

  constructor(props: Props) {
    super(props);
    this.registerItemRef = this.registerItemRef.bind(this);
    this.handleImageLoaded = this.handleImageLoaded.bind(this);
    this.handleLoadingDone = this.handleLoadingDone.bind(this);
    this.debouncedUpdate = debounce(this.debouncedUpdate.bind(this), 100);
  }

  render() {
    const { postList, availableWidth, children, touch, titleTile = false, itemGridIsInExitState } = this.props;
    // Update maps
    const newPostsIds = postList.map(post => PostListItem.getId(post));
    MzMap.map(this.loadedImages, (val, key) => {
      if (newPostsIds.indexOf(key) === -1) {
        return false;
      }
      return val;
    });
    MzMap.map(this.loadingDone, (val, key) => {
      if (newPostsIds.indexOf(key) === -1) {
        return false;
      }
      return val;
    });
    MzMap.map(this.itemsRefs, (val, key) => {
      if (newPostsIds.indexOf(key) === -1) {
        return undefined;
      }
      return val;
    });

    const spaceBetween: number = 20;
    const poses = postList.map(post => {
      const postId = PostListItem.getId(post);
      if (this.props.requestedPostId !== null) {
        if (postId === this.props.requestedPostId) {
          return 'requested';
        }
        return 'not-requested';
      }
      if (itemGridIsInExitState) {
        return 'not-requested';
      }
      if (!MzMap.get(this.itemsRefs, postId, undefined)) {
        return 'init';
      }
      if (!MzMap.get(this.loadedImages, postId, false) || !MzMap.get(this.loadingDone, postId, false)) {
        return 'loading';
      }
      return 'done';
    });
    const descHeightByIndex = postList.map((post, index) => {
      const noHeight = ['done', 'not-requested'].indexOf(poses[index]) === -1;
      if (noHeight) {
        return 0;
      }
      return MzMap.get(this.postsDescriptionHeight, PostListItem.getId(post), 0);
    });
    const gridResult = GridUtils.getGrid({
      fixedRatio: 350 / 250, // set false to use ratio from element
      availableWidth,
      ratioList: postList.map(post => PostListItem.getOverviewRatio(post)),
      additionalHeights: touch ? descHeightByIndex : [],
      minColNum: 2,
      idealColWidth: 270,
      spaceBetween,
      spaceAround: [0, 0, 0, 0],
      getInitialColums: (numOfCol, colSize) => {
        const cols = fill(Array(numOfCol), 0);
        const titleTileHeightRatio = this.props.titleTileHeightRatio || 1;
        if (titleTile) {
          if (numOfCol <= 2) {
            cols[0] = colSize * titleTileHeightRatio + spaceBetween;
          } else {
            cols[0] = colSize * titleTileHeightRatio + spaceBetween;
            cols[1] = colSize * titleTileHeightRatio + spaceBetween;
          }
        }
        return cols;
      },
    });
    return children(gridResult, poses, this.handleImageLoaded, this.handleLoadingDone, this.registerItemRef);
  }

  componentWillUnmount() {
    MzMap.forEach(this.observers, ro => {
      ro.disconnect();
    });
    this.unmounted = true;
  }

  private debouncedUpdate() {
    if (this.unmounted === false) {
      this.forceUpdate();
    }
  }

  private handleImageLoaded(postId: number): void {
    MzMap.set(this.loadedImages, postId, true);
    this.debouncedUpdate();
  }

  private handleLoadingDone(postId: number): void {
    MzMap.set(this.loadingDone, postId, true);
    this.debouncedUpdate();
  }

  private registerItemRef(postId: number, ref: HTMLElement | null): void {
    if (ref === null) {
      return;
    }
    const oldRef = MzMap.get(this.itemsRefs, postId, undefined);
    const oldRo = MzMap.get(this.observers, postId, undefined);
    let ro;
    if (oldRo === undefined) {
      ro = new ResizeObserver(
        debounce((entries, observer) => {
          if (entries.length !== 1) {
            console.warn('There should be only one entry');
            return;
          }
          const { height } = entries[0].contentRect;
          const oldHeight = MzMap.get(this.postsDescriptionHeight, postId, undefined);
          if (height !== oldHeight) {
            MzMap.set(this.postsDescriptionHeight, postId, height);
            this.debouncedUpdate();
          }
        }, 100)
      );
      MzMap.set(this.observers, postId, ro);
    } else {
      ro = oldRo;
    }
    if (oldRef !== undefined) {
      ro.unobserve(oldRef);
    }
    ro.observe(ref);
    MzMap.set(this.itemsRefs, postId, ref);
  }
}



// WEBPACK FOOTER //
// ./src/module/View/ItemsGrid/GridProvider.tsx